import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

// Module-level guard: OAuth authorization codes are single-use. React StrictMode
// runs effects twice in dev, and any double-mount would otherwise consume the
// code on the first call and fail the second with "code expired", clobbering
// the success UI. We cache the in-flight Promise itself (not a status enum) so
// every invocation awaits the *same* result.
const _inflight = new Map(); // code -> Promise<{status, msg}>

export const OAuthCallbackPage = () => {
  const { platform } = useParams();
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  const oauthError = params.get('error');
  const navigate = useNavigate();
  const { api } = useAuth();
  const [status, setStatus] = useState('processing');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const finalize = async () => {
      if (oauthError) {
        setStatus('error');
        setMsg(params.get('error_description') || oauthError);
        return;
      }
      if (!code || !state) {
        setStatus('error');
        setMsg('Missing code or state');
        return;
      }

      let promise = _inflight.get(code);
      if (!promise) {
        promise = (async () => {
          try {
            const res = await api.post(
              `/connections/${platform}/callback`,
              null,
              { params: { code, state } }
            );
            return {
              status: 'success',
              msg: `Connected as ${res.data.account_name || 'your account'}`,
            };
          } catch (e) {
            const detail = e.response?.data?.detail || 'Failed to complete OAuth';
            // If code was already consumed (e.g., user reloaded the callback URL),
            // the connection likely already succeeded. Treat as a soft error and
            // bounce them back to /connections so they can see real state.
            const stale =
              /expired|invalid_grant|already|used|consumed/i.test(detail);
            return {
              status: stale ? 'stale' : 'error',
              msg: stale
                ? 'This authorization link was already used — redirecting...'
                : detail,
            };
          }
        })();
        _inflight.set(code, promise);
      }

      const result = await promise;
      setStatus(result.status === 'stale' ? 'success' : result.status);
      setMsg(result.msg);
      if (result.status === 'success' || result.status === 'stale') {
        const ret = sessionStorage.getItem('oauth_return') || '/connections';
        sessionStorage.removeItem('oauth_return');
        setTimeout(() => navigate(ret, { replace: true }), 1200);
      }
    };
    finalize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, code, state, oauthError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4" data-testid="oauth-callback-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">Finishing connection...</h2>
              <p className="text-sm text-zinc-500 mt-2">Linking your {platform} account</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">All set!</h2>
              <p className="text-sm text-zinc-500 mt-2">{msg}</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">Couldn't connect</h2>
              <p className="text-sm text-zinc-500 mt-2 mb-6">{msg}</p>
              <Button onClick={() => navigate('/connections')} data-testid="oauth-callback-back-btn">Back to connections</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthCallbackPage;
